import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { TelephonyAgentEvent, TelephonyCallStage, TelephonyDirection, TelephonyToastState, TelephonyIdentifyCandidate, TelephonySubjectType } from '../types/telephony.events';
import { crmPhone } from '../services/crmphone.service';
import presenceStatusesService from '../services/presence-statuses.service';
import { SharedWebSocket } from '../utils/shared-websocket';
import authService from '../services/auth.service';
import { getVersionOutdated } from '../utils/versionOutdated';
import { getCallQueueCallbacks } from './CallQueueContext';
import { transformApiToLocal } from '../utils/call-queue-transformers';
import type {
  CallQueueAddedEventData,
  CallQueueUpdatedEventData,
  CallQueueRemovedEventData,
  CallQueueSyncEventData,
} from '../types/call-queue-api.types';

const WIDGET_OWNER_KEY = 'crmphone.widgetOwner';
const OWNER_TIMEOUT_MS = 15000;
const OWNER_HEARTBEAT_MS = 5000;

interface WidgetOwnerInfo {
  tabId: string;
  ts: number;
}

function readOwner(): WidgetOwnerInfo | null {
  try {
    const raw = localStorage.getItem(WIDGET_OWNER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export interface CallLocalState {
  resultCode?: string;
  comment?: string;
  seen?: boolean;
}

interface TelephonyAgentEventsContextValue {
  // Активные тосты (по callId)
  toasts: TelephonyToastState[];
  activeCallId: string | null;
  // Управление отображением
  minimize: (callId: string) => void;
  restore: (callId: string) => void;
  close: (callId: string) => void;
  chooseCustomer: (callId: string, customer: TelephonyIdentifyCandidate) => void;
  setActiveCall: (callId: string | null) => void;
  getCallState: (callId: string) => CallLocalState;
  updateCallState: (callId: string, patch: Partial<CallLocalState>) => void;
  // Dock режим (единый блок с панелью софтфона)
  docked: boolean;
  dock: () => void;
  undock: () => void;
  setPanelUserHidden: (v: boolean) => void;
  // Навигационные события (open_customer_card)
  navSeq: number;
  lastNav?: { seq: number; callId: string; phone: string; direction: TelephonyDirection; customerId?: number } | null;
  // Навигация к subject (agreement/lead/ticket)
  subjectNavEvent?: {
    seq: number;
    callId: string;
    subjectType: TelephonySubjectType;
    subjectId: string | number;
  } | null;
  // Диагностика
  connected: boolean;
  widgetOwnedByOtherTab: boolean;
}

const Ctx = createContext<TelephonyAgentEventsContextValue | undefined>(undefined);

export const useTelephonyAgentEvents = (): TelephonyAgentEventsContextValue => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTelephonyAgentEvents must be used within TelephonyAgentEventsProvider');
  return ctx;
};

export const useOptionalTelephonyAgentEvents = (): TelephonyAgentEventsContextValue | null => {
  const ctx = useContext(Ctx);
  return ctx ?? null;
};

export const TelephonyAgentEventsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { tokens, profile } = useAuth();
  const enabled = String(import.meta.env.VITE_CRM_PHONE_ENABLED ?? 'false').toLowerCase() === 'true';
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<SharedWebSocket | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const reconnectRef = useRef<number>(0);

  const [toastsMap, setToastsMap] = useState<Map<string, TelephonyToastState>>(new Map());
  const [docked, setDocked] = useState<boolean>(false);
  const [, setPanelUserHidden] = useState<boolean>(false);
  const [navSeq, setNavSeq] = useState(0);
  const navSeqRef = useRef(0);
  const lastNavRef = useRef<TelephonyAgentEventsContextValue['lastNav']>(null);
  const [lastNav, setLastNav] = useState<TelephonyAgentEventsContextValue['lastNav']>(null);
  const [subjectNavEvent, setSubjectNavEvent] = useState<TelephonyAgentEventsContextValue['subjectNavEvent']>(null);
  // Храним callId звонков, для которых была навигация по subject (НЕ создаём для них toast)
  const subjectNavCallIdsRef = useRef<Set<string>>(new Set());
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [callStateMap, setCallStateMap] = useState<Map<string, CallLocalState>>(new Map());
  const [postCallStatusId, setPostCallStatusId] = useState<number | null>(null);
  const settingPostCallRef = useRef<boolean>(false);
  const postCallAppliedRef = useRef<boolean>(false);
  const activeCallIdRef = useRef<string | null>(null);

  const tabIdRef = useRef(crypto.randomUUID());

  // Per-tab: виджет активен в этой вкладке
  const [widgetActive, setWidgetActive] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('crmphone.widgetActive') === 'true';
    } catch { return false; }
  });

  // Cross-tab: виджет занят ДРУГОЙ вкладкой
  const [ownedByOtherTab, setOwnedByOtherTab] = useState<boolean>(() => {
    const owner = readOwner();
    if (!owner) return false;
    if (owner.tabId === tabIdRef.current) return false;
    return Date.now() - owner.ts < OWNER_TIMEOUT_MS;
  });

  const setActiveCall = useCallback((callId: string | null) => {
    setActiveCallId(callId);
    if (callId) {
      setCallStateMap((prev) => {
        const next = new Map(prev);
        const state = next.get(callId) ?? {};
        if (!state.seen) {
          next.set(callId, { ...state, seen: true });
        }
        return next;
      });
    }
  }, []);

  const upsertToast = useCallback(
    (
      payload: {
        callId: string;
        direction: TelephonyDirection;
        phone: string;
        customerId?: number;
        customerFullName?: string;
        customerIin?: string;
        identifyCandidates?: TelephonyIdentifyCandidate[];
        telephonyEvent?: TelephonyCallStage;
        agentExtension?: string;
        agentId?: string;
        recordingUrl?: string | null;
        recordingReady?: boolean;
        selectedCustomerId?: number;
      },
      options?: { createIfMissing?: boolean }
    ) => {
      const { createIfMissing = true } = options ?? {};
      setToastsMap((prev) => {
        const existed = prev.get(payload.callId);
        const isNew = !existed;
        if (!existed && !createIfMissing) return prev;
        const next = new Map(prev);
        next.set(payload.callId, {
          callId: payload.callId,
          direction: payload.direction ?? existed?.direction ?? null,
          phone: payload.phone ?? existed?.phone ?? '',
          customerId: payload.customerId ?? existed?.customerId,
          customerFullName:
            payload.customerFullName !== undefined
              ? payload.customerFullName ?? undefined
              : existed?.customerFullName,
          customerIin: payload.customerIin ?? existed?.customerIin,
          identifyCandidates: payload.identifyCandidates ?? existed?.identifyCandidates,
          selectedCustomerId: payload.selectedCustomerId ?? existed?.selectedCustomerId,
          openCard: existed?.openCard ?? false,
          minimized: existed?.minimized ?? false,
          createdAt: existed?.createdAt ?? Date.now(),
          telephonyEvent: payload.telephonyEvent ?? existed?.telephonyEvent,
          agentExtension: payload.agentExtension ?? existed?.agentExtension,
          agentId: payload.agentId ?? existed?.agentId,
          recordingUrl:
            payload.recordingUrl !== undefined ? payload.recordingUrl ?? undefined : existed?.recordingUrl,
          recordingReady:
            typeof payload.recordingReady === 'boolean' ? payload.recordingReady : existed?.recordingReady,
        });
        if (isNew) {
          setCallStateMap((prevState) => {
            const nextState = new Map(prevState);
            if (!nextState.has(payload.callId)) {
              nextState.set(payload.callId, {});
            }
            return nextState;
          });
          if (!activeCallIdRef.current) {
            setActiveCall(payload.callId);
          }
        }
        return next;
      });
    },
    [setActiveCall]
  );

  const removeToast = useCallback((callId: string) => {
    setToastsMap((prev) => {
      if (!prev.has(callId)) return prev;
      const next = new Map(prev);
      next.delete(callId);
      return next;
    });
    setCallStateMap((prev) => {
      if (!prev.has(callId)) return prev;
      const next = new Map(prev);
      next.delete(callId);
      return next;
    });
  }, []);

  const minimize = useCallback((callId: string) => {
    setToastsMap((prev) => {
      const ex = prev.get(callId);
      if (!ex) return prev;
      const next = new Map(prev);
      next.set(callId, { ...ex, minimized: true });
      return next;
    });
  }, []);
  const restore = useCallback((callId: string) => {
    setToastsMap((prev) => {
      const ex = prev.get(callId);
      if (!ex) return prev;
      const next = new Map(prev);
      next.set(callId, { ...ex, minimized: false });
      return next;
    });
  }, []);
  const close = useCallback((callId: string) => {
    removeToast(callId);
    try {
      if (docked) crmPhone.hide();
    } catch (error) {
      void error;
    }
  }, [removeToast, docked]);

  useEffect(() => {
    activeCallIdRef.current = activeCallId;
  }, [activeCallId]);

  useEffect(() => {
    navSeqRef.current = navSeq;
  }, [navSeq]);

  // Per-tab: подписка на crmphone:visibility (same-tab)
  useEffect(() => {
    const handleCrmVisibility = (ev: Event) => {
      try {
        const visible = (ev as CustomEvent<{ visible: boolean }>).detail?.visible;
        if (typeof visible === 'boolean') setWidgetActive(visible);
      } catch { /* noop */ }
    };
    window.addEventListener('crmphone:visibility', handleCrmVisibility as EventListener);
    return () => window.removeEventListener('crmphone:visibility', handleCrmVisibility as EventListener);
  }, []);

  // Ownership claim/release: текущая вкладка публикует heartbeat в localStorage
  useEffect(() => {
    if (!widgetActive) return;

    const myTabId = tabIdRef.current;
    const claim = () => {
      try {
        localStorage.setItem(WIDGET_OWNER_KEY, JSON.stringify({ tabId: myTabId, ts: Date.now() }));
      } catch { /* noop */ }
    };

    // Claim immediately + start heartbeat
    claim();
    const heartbeat = window.setInterval(claim, OWNER_HEARTBEAT_MS);

    // Release on tab close
    const handleUnload = () => {
      try {
        const owner = readOwner();
        if (owner?.tabId === myTabId) localStorage.removeItem(WIDGET_OWNER_KEY);
      } catch { /* noop */ }
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload(); // release when widgetActive becomes false
    };
  }, [widgetActive]);

  // Мониторинг ownership из других вкладок
  useEffect(() => {
    const myTabId = tabIdRef.current;

    const checkOwnership = () => {
      if (widgetActive) { setOwnedByOtherTab(false); return; }
      const owner = readOwner();
      if (!owner) { setOwnedByOtherTab(false); return; }
      if (owner.tabId === myTabId) { setOwnedByOtherTab(false); return; }
      setOwnedByOtherTab(Date.now() - owner.ts < OWNER_TIMEOUT_MS);
    };

    // storage event fires in OTHER tabs when localStorage changes
    const handleStorage = (e: StorageEvent) => {
      if (e.key === WIDGET_OWNER_KEY) checkOwnership();
    };
    window.addEventListener('storage', handleStorage);

    // Periodic check for stale ownership (tab crash detection)
    const interval = window.setInterval(checkOwnership, OWNER_HEARTBEAT_MS);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(interval);
    };
  }, [widgetActive]);

  const getCallState = useCallback((callId: string): CallLocalState => {
    return callStateMap.get(callId) ?? {};
  }, [callStateMap]);

  const updateCallState = useCallback((callId: string, patch: Partial<CallLocalState>) => {
    setCallStateMap((prev) => {
      const next = new Map(prev);
      const current = next.get(callId) ?? {};
      next.set(callId, { ...current, ...patch });
      return next;
    });
  }, []);

  const chooseCustomer = useCallback((callId: string, customer: TelephonyIdentifyCandidate) => {
    setToastsMap((prev) => {
      const ex = prev.get(callId);
      if (!ex) return prev;
      const next = new Map(prev);
      next.set(callId, {
        ...ex,
        customerId: customer.id || ex.customerId,
        customerFullName: (customer.fullName || customer.full_name || customer.name) ?? ex.customerFullName,
        customerIin: customer.iin ?? ex.customerIin,
        selectedCustomerId: customer.id || ex.selectedCustomerId,
        // сохраняем список кандидатов, чтобы можно было вернуться к выбору
        identifyCandidates: ex.identifyCandidates,
        openCard: true,
      });
      return next;
    });
  }, []);


  const dock = useCallback(() => {
    // Авто‑докинг отключён: оставляем карточку в плавающем режиме
    setPanelUserHidden(false);
    setDocked(false);
  }, []);
  const undock = useCallback(() => {
    // Авто‑докинг отключён
    setDocked(false);
  }, []);

  useEffect(() => {
    if (!enabled || !tokens) {
      // Очистка, если выключено
      try { wsRef.current?.close(); } catch (error) { void error; }
      wsRef.current = null;
      setConnected(false);
      setActiveCallId(null);
      return;
    }
    if (!widgetActive) {
      // Виджет неактивен в этой вкладке — закрываем WS, но НЕ очищаем activeCallId
      try { wsRef.current?.close(); } catch (error) { void error; }
      wsRef.current = null;
      setConnected(false);
      return;
    }
    let stopped = false;

    const connect = async () => {
      if (stopped) return;
      if (!widgetActive) {
        console.log('[TelephonyAgentEventsContext] Пропуск подключения WebSocket - виджет неактивен');
        return;
      }
      // Не подключаемся если версия устарела
      if (getVersionOutdated()) {
        console.log('[TelephonyAgentEventsContext] Пропуск подключения WebSocket - версия устарела');
        return;
      }
      try {
        // Закрываем предыдущий SharedWebSocket чтобы освободить TabLeaderElection и интервалы
        if (wsRef.current) {
          try { wsRef.current.close(); } catch { /* ignore */ }
          wsRef.current = null;
        }

        const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${scheme}://${window.location.host}/api/v1/telephony/ws/agent-events`;

        // Получаем access token для передачи в WebSocket через Sec-WebSocket-Protocol
        // НЕ вызываем refreshToken() - authService обновляет токены автоматически
        const tokens = authService.getTokens();

        // Если токена нет - не подключаемся
        if (!tokens?.access_token) {
          console.warn('[TelephonyAgentEventsContext] No access token available');
          return;
        }
        const accessToken = tokens?.access_token;

        // Передаем токен через протокол для корректной авторизации на бэкенде
        // Формат: ['access_token', '<jwt_token>'] - бэкенд вернет 'access_token' обратно
        const protocols = accessToken ? ['access_token', accessToken] : undefined;
        // Отключаем автопереподключение в SharedWebSocket, так как у нас своя логика в onclose
        const ws = new SharedWebSocket(url, protocols, { autoReconnect: false });
        wsRef.current = ws;

        ws.onopen = () => {
          reconnectRef.current = 0;
          setConnected(true);
          // keep-alive ping согласно рекомендации бэкенда (30 секунд)
          try { if (pingTimerRef.current) window.clearInterval(pingTimerRef.current); } catch (error) { void error; }
          pingTimerRef.current = window.setInterval(() => {
        try {
          // Не отправляем ping если версия устарела
          if (getVersionOutdated()) {
            console.log('[TelephonyAgentEventsContext] Пропуск ping - версия устарела');
            return;
          }
          ws.send('{"type":"ping"}');
        } catch (error) { void error; }
          }, 30000);
        };

        ws.onmessage = async (ev) => {
          try {
            const msg = JSON.parse(ev.data) as TelephonyAgentEvent | { type?: string };

            // Если токен истек - обновляем токен и переподключаемся
            // Бэкенд присылает: {"event":"error","detail":"token_expired"}
            if ((msg as any).event === 'error' && (msg as any).detail === 'token_expired') {
              console.warn('[TelephonyAgentEventsContext] Token expired, refreshing token...');
              try {
                // Запускаем refresh токена
                await authService.refreshToken();
                console.log('[TelephonyAgentEventsContext] Token refreshed successfully, will reconnect');
              } catch (refreshError) {
                console.error('[TelephonyAgentEventsContext] Failed to refresh token:', refreshError);
              }
              // Закрываем соединение - автоматический reconnect подхватит новый токен
              try {
                ws.close();
              } catch (error) {
                console.error('[TelephonyAgentEventsContext] Failed to close ws after token expiry', error);
              }
              return;
            }

            if ((msg as any).type === 'pong') return;
            if (!('event' in msg)) return;
            const { event, callId, direction, phone } = msg as TelephonyAgentEvent;
            if (event === 'incoming_toast') {
              const m = msg as any;
              const subjectType = m.subjectType as TelephonySubjectType | undefined;
              const subjectId = m.subjectId;

              console.log('[TelephonyAgentEventsContext] incoming_toast:', { subjectType, subjectId, callId });

              // Если есть subject - только навигация, БЕЗ карточки звонка, но ДОБАВЛЯЕМ в очередь
              if (subjectType && subjectId) {
                // Защита от дублирования события incoming_toast (WebSocket может прислать дважды)
                if (subjectNavCallIdsRef.current.has(callId)) {
                  console.log('[TelephonyAgentEventsContext] Duplicate incoming_toast ignored:', callId);
                  return;
                }
                console.log('[TelephonyAgentEventsContext] Subject detected, navigating instead of toast');
                // Запоминаем callId, чтобы блокировать toast от других событий (identify_customer и т.д.)
                subjectNavCallIdsRef.current.add(callId);

                // НОВОЕ: Добавляем звонок в очередь для сохранения карточки
                const queueCallbacks = getCallQueueCallbacks();
                if (queueCallbacks) {
                  const m = msg as any;
                  // Извлекаем stageCode для загрузки списка действий по этапу договора
                  const stageCode = typeof m.stageCode === 'string' ? m.stageCode : undefined;
                  queueCallbacks.addToQueue({
                    callId,
                    phone,
                    direction,
                    subjectType,
                    subjectId,
                    stageCode,
                    customerId: m.customerId as number | undefined,
                    customerFullName: m.customerFullName as string | undefined,
                    customerIin: m.customerIin as string | undefined,
                    telephonyEvent: typeof m.telephonyEvent === 'string' ? (m.telephonyEvent as TelephonyCallStage) : undefined,
                  });
                  console.log('[TelephonyAgentEventsContext] Added call to queue:', callId, 'stageCode:', stageCode);
                }

                const seq = navSeqRef.current + 1;
                setNavSeq(seq);
                setSubjectNavEvent({ seq, callId, subjectType, subjectId });
                return; // Не создаём toast
              }

              // Стандартное поведение - создаём карточку звонка
              const telephonyEvent = typeof m.telephonyEvent === 'string' ? (m.telephonyEvent as TelephonyCallStage) : undefined;
              const agentExtension = typeof m.agentExtension === 'string' ? m.agentExtension : undefined;
              const agentId = typeof m.agentId === 'string' ? m.agentId : undefined;
              const candidate = (direction === 'outbound' || direction === 'dialer') ? crmPhone.consumePendingCustomer(phone) : undefined;
              const candidateName = candidate?.fullName ?? candidate?.full_name ?? candidate?.name;
              upsertToast({
                callId,
                direction,
                phone,
                customerId: (candidate?.id ?? m.customerId) as number | undefined,
                customerFullName: (candidateName ?? m.customerFullName) as string | undefined,
                customerIin: (candidate?.iin ?? m.customerIin) as string | undefined,
                selectedCustomerId: candidate?.id,
                identifyCandidates: candidate ? [candidate] : undefined,
                telephonyEvent,
                agentExtension,
                agentId,
              });
              if (candidate) {
                try { chooseCustomer(callId, candidate); } catch { /* noop */ }
              }
            } else if (event === 'close_toast') {
              // Очищаем callId из subject навигации (звонок завершён)
              const wasSubjectCall = subjectNavCallIdsRef.current.has(callId);
              subjectNavCallIdsRef.current.delete(callId);
              const m = msg as any;
              const telephonyEvent = typeof m.telephonyEvent === 'string' ? (m.telephonyEvent as TelephonyCallStage) : undefined;
              const agentExtension = typeof m.agentExtension === 'string' ? m.agentExtension : undefined;
              const agentId = typeof m.agentId === 'string' ? m.agentId : undefined;
              const recordingUrl = typeof m.recordingUrl === 'string' ? m.recordingUrl : undefined;
              const recordingReady = typeof m.recordingReady === 'boolean' ? m.recordingReady : undefined;

              // НОВОЕ: Обновляем статус в очереди при завершении звонка
              if (wasSubjectCall) {
                const queueCallbacks = getCallQueueCallbacks();
                if (queueCallbacks) {
                  queueCallbacks.updateCall(callId, {
                    status: 'pending_result',
                    telephonyEvent,
                    recordingUrl,
                    recordingReady,
                  });
                  console.log('[TelephonyAgentEventsContext] Updated call in queue to pending_result:', callId);
                }
              }

              upsertToast(
                {
                  callId,
                  direction,
                  phone,
                  telephonyEvent,
                  agentExtension,
                  agentId,
                  recordingUrl,
                  recordingReady,
                },
                { createIfMissing: false }
              );
              // Игнорируем автоматическое закрытие карточки по событию от виджета
              // (оставляем карточку на экране до явного действия пользователя)
            } else if (event === 'identify_customer') {
              // Если для этого callId была навигация по subject - не создаём toast
              if (subjectNavCallIdsRef.current.has(callId)) {
                console.log('[TelephonyAgentEventsContext] identify_customer blocked for subject call:', callId);
                return;
              }
              const m = msg as any;
              const candidates = Array.isArray((m as any).candidates) ? (m as any).candidates as TelephonyIdentifyCandidate[] : [];
              const telephonyEvent = typeof m.telephonyEvent === 'string' ? (m.telephonyEvent as TelephonyCallStage) : undefined;
              const agentExtension = typeof m.agentExtension === 'string' ? m.agentExtension : undefined;
              const agentId = typeof m.agentId === 'string' ? m.agentId : undefined;
              upsertToast({ callId, direction, phone, identifyCandidates: candidates, telephonyEvent, agentExtension, agentId });
            } else if (event === 'open_customer_card') {
              const m = msg as any;
              const seq = navSeqRef.current + 1;
              setNavSeq(seq);
              const nav = { seq, callId, phone, direction, customerId: m.customerId as number | undefined };
              lastNavRef.current = nav;
              setLastNav(nav);
              // Обновим детали клиента в существующем тосте (если он есть)
              setToastsMap((prev) => {
                const existed = prev.get(callId);
                if (!existed) return prev;
                const next = new Map(prev);
                next.set(callId, {
                  ...existed,
                  customerId: (m.customerId ?? existed.customerId) as number | undefined,
                  customerFullName: (m.customerFullName ?? existed.customerFullName) as string | undefined,
                  customerIin: (m.customerIin ?? existed.customerIin) as string | undefined,
                  telephonyEvent:
                    typeof m.telephonyEvent === 'string'
                      ? (m.telephonyEvent as TelephonyCallStage)
                      : existed.telephonyEvent,
                  agentExtension:
                    typeof m.agentExtension === 'string' ? (m.agentExtension as string) : existed.agentExtension,
                  agentId: typeof m.agentId === 'string' ? (m.agentId as string) : existed.agentId,
                });
                return next;
              });
            }

            // Обработка событий Call Queue (real-time sync)
            const msgType = (msg as any).type;

            if (msgType === 'call_queue:added') {
              const queueCallbacks = getCallQueueCallbacks();
              const data = (msg as any).data as CallQueueAddedEventData;
              if (queueCallbacks?.onCallAdded && data) {
                const localCall = transformApiToLocal(data);
                queueCallbacks.onCallAdded(localCall);
                console.log('[TelephonyAgentEventsContext] call_queue:added', data.call_id);
              }
            } else if (msgType === 'call_queue:updated') {
              const queueCallbacks = getCallQueueCallbacks();
              const data = (msg as any).data as CallQueueUpdatedEventData;
              if (queueCallbacks?.onCallUpdated && data?.call_id) {
                // Если пришёл полный объект с id - трансформируем
                if (data.id) {
                  const localCall = transformApiToLocal(data as any);
                  queueCallbacks.onCallUpdated(data.call_id, localCall);
                } else if (data.changes) {
                  // Если пришли только изменения
                  const updates: Record<string, any> = {};
                  if (data.changes.status) updates.status = data.changes.status;
                  if (data.changes.telephony_event) updates.telephonyEvent = data.changes.telephony_event;
                  if (data.changes.recording_url !== undefined) updates.recordingUrl = data.changes.recording_url;
                  if (data.changes.recording_ready !== undefined) updates.recordingReady = data.changes.recording_ready;
                  if (data.changes.seen !== undefined) updates.seen = data.changes.seen;
                  queueCallbacks.onCallUpdated(data.call_id, updates);
                }
                console.log('[TelephonyAgentEventsContext] call_queue:updated', data.call_id);
              }
            } else if (msgType === 'call_queue:removed') {
              const queueCallbacks = getCallQueueCallbacks();
              const data = (msg as any).data as CallQueueRemovedEventData;
              if (queueCallbacks?.onCallRemoved && data?.call_id) {
                queueCallbacks.onCallRemoved(data.call_id);
                console.log('[TelephonyAgentEventsContext] call_queue:removed', data.call_id, 'reason:', data.reason);
              }
            } else if (msgType === 'call_queue:sync') {
              const queueCallbacks = getCallQueueCallbacks();
              const data = (msg as any).data as CallQueueSyncEventData;
              if (queueCallbacks?.onFullSync && data?.items) {
                const localCalls = data.items.map(transformApiToLocal);
                queueCallbacks.onFullSync(localCalls);
                console.log('[TelephonyAgentEventsContext] call_queue:sync', localCalls.length, 'items');
              }
            }
          } catch (e) {
            // noop
          }
        };

        ws.onclose = () => {
          // Обнуляем ref только если он всё ещё указывает на этот экземпляр
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          setConnected(false);
          try { if (pingTimerRef.current) window.clearInterval(pingTimerRef.current); } catch (error) { void error; }
          pingTimerRef.current = null;
          if (!stopped) {
            const n = Math.min(reconnectRef.current + 1, 6);
            reconnectRef.current = n;
            setTimeout(connect, n * 1000);
          }
        };
        ws.onerror = () => {
        try { ws.close(); } catch (error) { void error; }
        };
      } catch (e) {
        const n = Math.min(reconnectRef.current + 1, 6);
        reconnectRef.current = n;
        setTimeout(connect, n * 1000);
      }
    };

    // Функция для умной проверки и переподключения
    const ensureConnection = () => {
      if (stopped || getVersionOutdated() || !widgetActive) return;

      // Если соединение уже активно - ничего не делать
      if (wsRef.current &&
          (wsRef.current.readyState === WebSocket.OPEN ||
           wsRef.current.readyState === WebSocket.CONNECTING)) {
        return;
      }

      // Переподключаться только если соединение закрыто
      console.log('[TelephonyAgentEventsContext] Переподключение при возвращении пользователя');
      connect();
    };

    // Обработчик возвращения пользователя
    const handleVisibilityChange = () => {
      if (!document.hidden && !stopped) {
        // Пользователь вернулся - проверяем соединение
        ensureConnection();
      }
    };

    // Начальное подключение
    connect();

    // Подписываемся на изменение видимости
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { wsRef.current?.close(); } catch (error) { void error; }
      wsRef.current = null;
      try { if (pingTimerRef.current) window.clearInterval(pingTimerRef.current); } catch (error) { void error; }
      pingTimerRef.current = null;
    };
  }, [enabled, tokens, widgetActive]);

  const toasts = useMemo(() => Array.from(toastsMap.values()).sort((a, b) => b.createdAt - a.createdAt), [toastsMap]);

  useEffect(() => {
    if (toasts.length === 0) {
      setActiveCall(null);
      return;
    }
    if (activeCallId && toasts.some((t) => t.callId === activeCallId)) return;
    const nextActive = toasts[0]?.callId ?? null;
    setActiveCall(nextActive);
  }, [toasts, activeCallId, setActiveCall]);

  useEffect(() => {
    const statuses = Array.isArray(profile?.presence_statuses) ? profile.presence_statuses : [];
    const found = statuses.find((s) => {
      const code = typeof s?.code === 'string' ? s.code : typeof s?.key === 'string' ? s.key : undefined;
      return code === 'post_call_work';
    });
    setPostCallStatusId(typeof found?.id === 'number' ? found.id : null);
  }, [profile?.presence_statuses]);

  useEffect(() => {
    if (!postCallStatusId) return;
    if (toasts.length >= 4) {
      if (postCallAppliedRef.current || settingPostCallRef.current) return;
      settingPostCallRef.current = true;
      (async () => {
        try {
          await presenceStatusesService.activatePresenceStatus(postCallStatusId);
          postCallAppliedRef.current = true;
        } catch (error) {
          console.error('Не удалось переключить статус на post_call_work', error);
        } finally {
          settingPostCallRef.current = false;
        }
      })();
    } else {
      postCallAppliedRef.current = false;
    }
  }, [toasts.length, postCallStatusId]);

  // Автодокинг и автопоказ панели отключены по требованиям

  const value = useMemo<TelephonyAgentEventsContextValue>(
    () => ({
      toasts,
      activeCallId,
      minimize,
      restore,
      close,
      chooseCustomer,
      setActiveCall,
      getCallState,
      updateCallState,
      docked,
      dock,
      undock,
      setPanelUserHidden,
      navSeq,
      lastNav,
      subjectNavEvent,
      connected,
      widgetOwnedByOtherTab: ownedByOtherTab,
    }),
    [
      toasts,
      activeCallId,
      minimize,
      restore,
      close,
      chooseCustomer,
      setActiveCall,
      getCallState,
      updateCallState,
      docked,
      dock,
      undock,
      setPanelUserHidden,
      navSeq,
      lastNav,
      subjectNavEvent,
      connected,
      ownedByOtherTab,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
